(() => {
  if (window.__lcaTabsCarousel) return;
  window.__lcaTabsCarousel = true;

  const states = new WeakMap();
  const MOBILE = '(max-width: 700px)';

  const flag = (value) => value === 'true' || value === '1';

  const findRoot = (target) => {
    if (!target || typeof target.querySelector !== 'function') return null;
    if (typeof target.matches === 'function' && target.matches('[data-lca-tabs-carousel]')) return target;
    return target.querySelector('[data-lca-tabs-carousel]');
  };

  const activePanel = (state) => state.root.querySelector('.lca-tabs-panel:not([hidden])');
  const activeTrack = (state) => {
    const panel = activePanel(state);
    return panel ? panel.querySelector('[data-lca-track]') : null;
  };

  const mobile = () => window.matchMedia(MOBILE).matches;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const cardStep = (track) => {
    const card = track.querySelector('.product-card');
    if (!card) return track.clientWidth || 0;
    const style = getComputedStyle(track);
    const gap = parseFloat(style.columnGap || style.gap) || 0;
    return card.getBoundingClientRect().width + gap;
  };

  const atStart = (track) => track.scrollLeft <= 2;
  const atEnd = (track) => track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;

  const stopAutoplay = (state) => {
    if (state.timer) {
      clearInterval(state.timer);
      state.timer = 0;
    }
  };

  const updateArrows = (state) => {
    const panel = activePanel(state);
    const track = activeTrack(state);
    if (!panel || !track) return;
    const prev = panel.querySelector('[data-lca-prev]');
    const next = panel.querySelector('[data-lca-next]');
    const count = track.querySelectorAll('.product-card:not([hidden])').length;
    const loop = state.loop && count > 1;
    if (prev) prev.disabled = count <= 1 || (!loop && atStart(track));
    if (next) next.disabled = count <= 1 || (!loop && atEnd(track));
  };

  const placeArrows = (state) => {
    const track = activeTrack(state);
    const viewport = track && track.closest('[data-lca-viewport]');
    const image = track && track.querySelector('.product-image');
    if (!viewport || !image) return;
    viewport.style.setProperty('--lca-arrow-band', `${Math.round(image.getBoundingClientRect().height)}px`);
  };

  const syncViewAll = (state, tab) => {
    const link = state.root.querySelector('[data-lca-view-all]');
    if (!link || !tab) return;
    const href = tab.dataset.viewAll || '';
    const label = tab.dataset.viewAllLabel || 'View all';
    const arrow = link.querySelector('span[aria-hidden="true"]');
    if (!href) {
      link.hidden = true;
      link.removeAttribute('href');
    } else {
      link.hidden = false;
      link.setAttribute('href', href);
    }
    [...link.childNodes].forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) node.remove();
    });
    link.insertBefore(document.createTextNode(`${label} `), arrow || null);
  };

  const advance = (state, direction) => {
    const track = activeTrack(state);
    if (!track) return;
    const count = track.querySelectorAll('.product-card:not([hidden])').length;
    if (count <= 1) return;
    const behavior = state.reduced ? 'auto' : 'smooth';
    const step = cardStep(track) * direction;
    if (direction > 0 && atEnd(track)) {
      if (state.loop) track.scrollTo({ left: 0, behavior });
      updateArrows(state);
      return;
    }
    if (direction < 0 && atStart(track)) {
      if (state.loop) track.scrollTo({ left: track.scrollWidth, behavior });
      updateArrows(state);
      return;
    }
    track.scrollBy({ left: step, behavior });
  };

  const startAutoplay = (state) => {
    stopAutoplay(state);
    if (!state.autoplay || state.paused || state.holding || state.reduced || document.hidden || !mobile()) return;
    const panel = activePanel(state);
    if (!panel) return;
    state.timer = window.setInterval(() => {
      if (panel.hidden || !mobile()) return;
      const track = panel.querySelector('[data-lca-track]');
      if (!track) return;
      const count = track.querySelectorAll('.product-card:not([hidden])').length;
      if (count <= 1) return;
      if (!state.loop && atEnd(track)) {
        stopAutoplay(state);
        return;
      }
      advance(state, 1);
    }, state.interval);
  };

  const userPause = (state) => {
    if (state.pauseAfter) {
      state.paused = true;
      stopAutoplay(state);
      return;
    }
    state.holding = true;
    stopAutoplay(state);
  };

  const activate = (state, tab, options = {}) => {
    if (!tab) return;
    const changed = state.current !== tab;
    state.current = tab;
    if (changed) state.paused = false;
    const tabs = [...state.root.querySelectorAll('[data-lca-tab]')];
    tabs.forEach((item) => {
      const on = item === tab;
      item.classList.toggle('active', on);
      item.setAttribute('aria-selected', on ? 'true' : 'false');
      item.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(item.getAttribute('aria-controls'));
      if (!panel) return;
      panel.hidden = !on;
      if (on) panel.removeAttribute('inert');
      else panel.setAttribute('inert', '');
    });
    syncViewAll(state, tab);
    if (!options.silent) {
      const status = state.root.querySelector('[data-lca-status]');
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      const count = panel ? panel.querySelectorAll('.product-card:not([hidden])').length : 0;
      if (status) status.textContent = `${tab.textContent.trim()} · ${count}`;
    }
    const track = activeTrack(state);
    if (track && changed) track.scrollLeft = 0;
    placeArrows(state);
    updateArrows(state);
    startAutoplay(state);
    if (options.focus) tab.focus();
  };

  const boot = (root) => {
    if (!root || root.dataset.lcaTabsReady === 'true') return;
    root.dataset.lcaTabsReady = 'true';
    const controller = new AbortController();
    const { signal } = controller;
    const state = {
      root,
      timer: 0,
      current: null,
      paused: false,
      holding: false,
      autoplay: flag(root.dataset.autoplay),
      loop: flag(root.dataset.loop),
      pauseAfter: flag(root.dataset.pause),
      interval: Math.min(10000, Math.max(2000, (Number(root.dataset.interval) || 5) * 1000)),
      reduced: reducedMotion(),
    };
    states.set(root, state);

    const first = root.querySelector('[data-lca-tab][aria-selected="true"]') || root.querySelector('[data-lca-tab]');
    if (first) activate(state, first, { silent: true });

    root.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-lca-tab]');
      if (tab && root.contains(tab)) {
        activate(state, tab);
        return;
      }
      if (event.target.closest('[data-lca-prev]')) {
        event.preventDefault();
        userPause(state);
        advance(state, -1);
      } else if (event.target.closest('[data-lca-next]')) {
        event.preventDefault();
        userPause(state);
        advance(state, 1);
      }
    }, { signal });

    root.addEventListener('keydown', (event) => {
      const tab = event.target.closest('[data-lca-tab]');
      if (tab && root.contains(tab)) {
        const tabs = [...root.querySelectorAll('[data-lca-tab]')];
        const index = tabs.indexOf(tab);
        let dest = null;
        if (event.key === 'ArrowRight') dest = tabs[(index + 1) % tabs.length];
        else if (event.key === 'ArrowLeft') dest = tabs[(index - 1 + tabs.length) % tabs.length];
        else if (event.key === 'Home') dest = tabs[0];
        else if (event.key === 'End') dest = tabs[tabs.length - 1];
        if (dest) {
          event.preventDefault();
          activate(state, dest, { focus: true });
        }
        return;
      }
      const track = event.target.closest('[data-lca-track]');
      if (!track || event.target !== track) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        userPause(state);
        advance(state, event.key === 'ArrowRight' ? 1 : -1);
      }
    }, { signal });

    root.addEventListener('pointerdown', (event) => {
      if (event.target.closest('[data-lca-track], [data-lca-prev], [data-lca-next]')) userPause(state);
    }, { signal });

    root.addEventListener('pointerup', () => {
      if (!state.pauseAfter && state.holding) {
        state.holding = false;
        startAutoplay(state);
      }
    }, { signal });

    root.addEventListener('pointercancel', () => {
      if (!state.pauseAfter && state.holding) {
        state.holding = false;
        startAutoplay(state);
      }
    }, { signal });

    root.querySelectorAll('[data-lca-track]').forEach((track) => {
      let startX = 0;
      let startY = 0;
      let dragged = false;
      track.addEventListener('touchstart', (event) => {
        const touch = event.changedTouches[0];
        startX = touch.clientX;
        startY = touch.clientY;
        dragged = false;
      }, { passive: true, signal });
      track.addEventListener('touchmove', (event) => {
        const touch = event.changedTouches[0];
        if (Math.hypot(touch.clientX - startX, touch.clientY - startY) > 8) dragged = true;
      }, { passive: true, signal });
      track.addEventListener('click', (event) => {
        if (!dragged) return;
        event.preventDefault();
        event.stopPropagation();
        dragged = false;
      }, { capture: true, signal });
      track.addEventListener('scroll', () => updateArrows(state), { passive: true, signal });
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAutoplay(state);
      else startAutoplay(state);
    }, { signal });

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onMotion = () => {
      state.reduced = motion.matches;
      startAutoplay(state);
    };
    if (motion.addEventListener) motion.addEventListener('change', onMotion, { signal });

    const widthQuery = window.matchMedia(MOBILE);
    const onWidth = () => {
      placeArrows(state);
      updateArrows(state);
      startAutoplay(state);
    };
    if (widthQuery.addEventListener) widthQuery.addEventListener('change', onWidth, { signal });
    window.addEventListener('resize', () => {
      placeArrows(state);
      updateArrows(state);
    }, { signal });

    state.destroy = () => {
      stopAutoplay(state);
      controller.abort();
      states.delete(root);
      delete root.dataset.lcaTabsReady;
    };
  };

  const destroy = (root) => {
    const state = root && states.get(root);
    if (state) state.destroy();
  };

  const initAll = (scope) => {
    const context = scope && scope.querySelectorAll ? scope : document;
    context.querySelectorAll('[data-lca-tabs-carousel]').forEach(boot);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document));
  else initAll(document);

  document.addEventListener('shopify:section:load', (event) => {
    const root = findRoot(event.target);
    if (root) boot(root);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(findRoot(event.target));
  });

  document.addEventListener('shopify:block:select', (event) => {
    const root = findRoot(event.target);
    const blockId = event.detail && event.detail.blockId;
    if (!root || !blockId) return;
    const state = states.get(root);
    if (!state) return;
    const tab = root.querySelector(`[data-lca-tab][data-block-id="${CSS.escape(blockId)}"]`);
    if (tab) activate(state, tab);
  });
})();
